import React from 'react';
import { Switch } from '@roger/ui-components';

import { useAdminSectionAccess } from '../../hooks/useAdminSectionAccess';
import { useAgencyConfigSection } from '../../hooks/useAgencyConfigSection';
import { resetAgencyReportPolicy } from '../../reporting/agencyReportPolicy';
import type { AgencyConfigPayload } from '../../services/agencyConfig';
import { SettingsEditFooter, SettingValue } from './SettingsSectionParts';

type Values = { exclude_invoice_claims_from_ar: boolean };

const DEFAULTS: Values = { exclude_invoice_claims_from_ar: false };

function fromConfig(cfg: AgencyConfigPayload): Values {
  return {
    exclude_invoice_claims_from_ar: cfg.exclude_invoice_claims_from_ar === true,
  };
}

// Only the toggle that moved: the same endpoint carries settings other screens own.
function diff(values: Values, saved: Values): Partial<Values> {
  return values.exclude_invoice_claims_from_ar ===
    saved.exclude_invoice_claims_from_ar
    ? {}
    : { exclude_invoice_claims_from_ar: values.exclude_invoice_claims_from_ar };
}

// A report run reads the setting once, so forget the stored answer on save.
function afterSave(values: Values): Values {
  resetAgencyReportPolicy();
  return values;
}

/**
 * Whether claims of invoice-billed insurances stay off the AR reports, for an
 * agency that tracks invoice receivables elsewhere. A report can opt back in.
 */
const InvoiceArReportSetting: React.FC = () => {
  const { canEdit } = useAdminSectionAccess('insurance');
  const section = useAgencyConfigSection({
    enabled: true,
    initial: DEFAULTS,
    fromConfig,
    diff,
    afterSave,
    successMessage: 'AR report setting updated',
    errorMessage: 'Failed to update the AR report setting',
  });
  const { values, setValues, isLoading, loadFailed } = section;
  const showDisplayView = !section.isEditing || !canEdit;
  const id = 'exclude_invoice_claims_from_ar';

  return (
    <div
      data-testid={`setting-${id}`}
      className='mb-4 shrink-0 space-y-3 rounded-md border p-3'
    >
      <div className='flex items-center justify-between gap-4'>
        <div className='space-y-0.5'>
          <label htmlFor={id} className='text-sm font-medium'>
            Leave invoice-billed claims off AR reports
          </label>
          <p className='text-xs text-muted-foreground'>
            The four AR reports skip claims of insurances billed by invoice: AR
            Aging, AR Report, Expected Payments by Day and AR by Billing Cycle.
            Revenue, Collectibility by Payer and custom reports still count
            them. An AR report can include them again in its population
            settings.
          </p>
          {loadFailed && (
            <p className='text-xs text-destructive'>
              This setting did not load. Reload the page to see it.
            </p>
          )}
        </div>
        <SettingValue isLoading={isLoading} loadFailed={loadFailed}>
          {!showDisplayView ? (
            <Switch
              id={id}
              checked={values.exclude_invoice_claims_from_ar}
              onCheckedChange={checked =>
                setValues({ exclude_invoice_claims_from_ar: checked })
              }
            />
          ) : (
            <span
              className={`shrink-0 whitespace-nowrap text-sm font-medium ${
                values.exclude_invoice_claims_from_ar
                  ? 'text-green-600'
                  : 'text-muted-foreground'
              }`}
            >
              {values.exclude_invoice_claims_from_ar ? 'Enabled' : 'Disabled'}
            </span>
          )}
        </SettingValue>
      </div>
      <SettingsEditFooter
        canEdit={canEdit}
        isEditing={section.isEditing}
        isSaving={section.isSaving}
        editDisabled={isLoading || loadFailed}
        onEdit={section.startEditing}
        onCancel={section.cancel}
        onSave={() => void section.save()}
      />
    </div>
  );
};

export default InvoiceArReportSetting;
